import React, { useState } from "react";
import { Bug, RadioTower, Video, AlertTriangle, CheckCircle, Info } from "lucide-react";

import bugFixApi from "../../api/bugFixApi";
import ristRestartApi from "../../api/ristRestartApi";
import manageCaptureApi from "../../api/manageCaptureApi";
import useGetActionLogs from "../../hooks/useGetActionLogs";
import { KeepAlive } from "../keep-alive";
import ActionLogList from "./ActionLogList";
import { Card, CardHeader, CardTitle, CardContent } from "components/ui/card";
import { Button } from "components/ui/button";
import { Badge } from "components/ui/badge";
import { Select } from "components/ui/select";

// Environments where each Step Function is deployed. Kept in step with
// supported_environments in health-check-api/src/config/step_function_config.json.
const BUG_FIX_ENVIRONMENTS = ["dcx", "dca", "dcb"];
const RIST_ENVIRONMENTS = ["dca", "dcb"];

// Overlay files accepted by the bug fix Step Function.
const OVERLAYS = [
    "DAZN_BUG_TR.png",
    "DAZN_BUG_TL.png",
    "DAZN_BUG_BR.png",
    "DAZN_BUG_BL.png",
    "FIFA_DAZN_BUG_TR.png",
];

const RIST_LEGS = ["Pri", "Sec"];

/**
 * Build the MediaConnect channel name the RIST Step Function expects,
 * e.g. environment "dca" + channelId 33 + leg "Sec" -> "CH33_DCA_Sec".
 */
function buildChannelName(environment, channelId, leg) {
    const channel = String(channelId).toUpperCase().startsWith("CH")
        ? String(channelId).toUpperCase()
        : `CH${channelId}`;
    return `${channel}_${environment.toUpperCase()}_${leg}`;
}

function StatusLine({ status }) {
    if (!status) {
        return null;
    }
    return (
        <div className="flex flex-wrap items-center gap-2">
            <Badge variant={status.success ? "success" : "destructive"}>
                {status.success ? (
                    <CheckCircle className="h-3.5 w-3.5" />
                ) : (
                    <AlertTriangle className="h-3.5 w-3.5" />
                )}
                {status.success ? "Started" : "Failed"}
            </Badge>
            <small className="text-muted-foreground">
                {status.timestamp} - {status.message}
            </small>
        </div>
    );
}

/**
 * Whether source capture is currently on, according to the newest successful
 * capture action. Derived from the shared log rather than this browser, so a
 * capture started by one operator is visible to the rest.
 */
function isCaptureRunning(captureEntries) {
    const lastSuccess = (captureEntries || []).find(
        (entry) => entry.status === "success"
    );
    return Boolean(lastSuccess) && lastSuccess.details?.capture === "on";
}

function UnsupportedNote({ children }) {
    return (
        <div className="flex items-start gap-2 rounded-md border border-primary/30 bg-primary/10 px-4 py-3 text-sm">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{children}</div>
        </div>
    );
}

/**
 * Actions Component
 *
 * Operational Step Function actions for the current event: start/stop source
 * capture, insert/remove the DAZN bug overlay, and restart the on-prem RIST
 * MediaConnect channels.
 */
const Actions = ({ eventId, environment, eventDetails }) => {
    const { logs, error: logsError, refetchActionLogs } = useGetActionLogs(eventId);

    const [captureState, setCaptureState] = useState({
        isLoading: false,
        error: null
    });

    const [bug, setBug] = useState("insert");
    const [overlay, setOverlay] = useState(OVERLAYS[0]);
    const [bugLoading, setBugLoading] = useState(false);
    const [bugStatus, setBugStatus] = useState(null);

    // Start with nothing selected so a restart is always a deliberate choice.
    const [legs, setLegs] = useState([]);
    const [ristLoading, setRistLoading] = useState(false);
    const [ristStatus, setRistStatus] = useState(null);

    const bugFixSupported = BUG_FIX_ENVIRONMENTS.includes(environment);
    const ristSupported = RIST_ENVIRONMENTS.includes(environment);
    const channelId = eventDetails ? eventDetails["channelId"] : null;
    const channels = channelId
        ? legs.map((leg) => buildChannelName(environment, channelId, leg))
        : [];

    const captureRunning = isCaptureRunning(logs.capture);

    const toggleLeg = (leg) => {
        setLegs((previous) =>
            previous.includes(leg)
                ? previous.filter((item) => item !== leg)
                : RIST_LEGS.filter((item) => previous.includes(item) || item === leg)
        );
    };

    const recordStatus = (setStatus, success, message) => {
        setStatus({
            success: success,
            message: message,
            timestamp: new Date().toLocaleTimeString(),
        });
    };

    async function handleCaptureToggle() {
        // Disable button during transition
        if (captureState.isLoading) {
            return;
        }

        // Only allow capture when event is running
        if (eventDetails && eventDetails["eventStatus"] !== "RUNNING") {
            setCaptureState(prev => ({
                ...prev,
                error: "Capture can only be started/stopped when event status is RUNNING"
            }));
            return;
        }

        const capture = captureRunning ? "off" : "on";

        // Set loading state
        setCaptureState(prev => ({
            ...prev,
            isLoading: true,
            error: null
        }));

        try {
            await manageCaptureApi(eventId, environment, capture);

            // The log is the source of truth for whether capture is running.
            await refetchActionLogs();
            setCaptureState(prev => ({
                ...prev,
                isLoading: false,
                error: null
            }));
        } catch (err) {
            let errorMsg = "An unexpected error occurred. Please try again.";

            if (err.message) {
                if (err.message.includes("already in progress") ||
                    err.message.includes("already exists")) {
                    // Already in the requested state; the log reflects it.
                    await refetchActionLogs();
                    setCaptureState(prev => ({
                        ...prev,
                        isLoading: false,
                        error: null
                    }));
                    return;
                } else if (err.message.includes("Access denied")) {
                    errorMsg = "Access denied to Step Function. Please check IAM permissions or contact the administrator.";
                } else if (err.message.includes("role not found")) {
                    errorMsg = "Cross-account role not found. Please contact the administrator.";
                } else if (err.message.includes("Invalid parameters")) {
                    errorMsg = "Invalid request parameters. Please check your input.";
                } else if (err.message.includes("Network error")) {
                    errorMsg = "Network error. Please check your connection and try again.";
                } else {
                    errorMsg = err.message;
                }
            }

            setCaptureState(prev => ({
                ...prev,
                isLoading: false,
                error: errorMsg
            }));
            await refetchActionLogs();
        }
    }

    async function handleBugFix() {
        setBugLoading(true);
        setBugStatus(null);
        try {
            const response = await bugFixApi(eventId, environment, bug, bug === "insert" ? overlay : "");
            recordStatus(setBugStatus, true, response.message || `Bug ${bug} started`);
        } catch (error) {
            recordStatus(setBugStatus, false, error.message || "An unexpected error occurred.");
        }
        await refetchActionLogs();
        setBugLoading(false);
    }

    async function handleRistRestart() {
        setRistLoading(true);
        setRistStatus(null);
        try {
            const response = await ristRestartApi(channels, environment, eventId);
            recordStatus(setRistStatus, true, response.message || "RIST restart started");
        } catch (error) {
            recordStatus(setRistStatus, false, error.message || "An unexpected error occurred.");
        }
        await refetchActionLogs();
        setRistLoading(false);
    }

    const eventRunning = eventDetails && eventDetails["eventStatus"] === "RUNNING";

    return (
        <div className="space-y-3">
            <KeepAlive
                eventId={eventId}
                environment={environment}
                entries={logs.keep_alive}
                logsError={logsError}
                onActionRecorded={refetchActionLogs}
            />

            <Card>
                <CardHeader className="border-b border-border">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Video className="h-4 w-4 text-primary" />
                        Source Capture
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-4">
                    <div className="flex flex-wrap items-center gap-3">
                        <Button
                            size="sm"
                            variant={captureRunning ? "outline-destructive" : "outline-success"}
                            onClick={handleCaptureToggle}
                            disabled={captureState.isLoading || !eventRunning}
                            className="min-w-[130px] font-medium"
                        >
                            {captureState.isLoading
                                ? (captureRunning ? "Stopping..." : "Starting...")
                                : captureRunning
                                ? "Stop Capture"
                                : "Start Capture"}
                        </Button>
                        {captureRunning && <Badge variant="success">Capturing</Badge>}
                    </div>
                    {!eventRunning && (
                        <small className="text-muted-foreground">
                            Capture can only be started/stopped when the event status is RUNNING.
                        </small>
                    )}
                    {captureState.error && (
                        <div className="flex flex-wrap items-center gap-2">
                            <Badge variant="destructive">
                                <AlertTriangle className="h-3.5 w-3.5" />
                                Failed
                            </Badge>
                            <small className="text-muted-foreground">{captureState.error}</small>
                        </div>
                    )}
                    <ActionLogList entries={logs.capture} error={logsError} />
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="border-b border-border">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <Bug className="h-4 w-4 text-primary" />
                        DAZN Bug
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-4">
                    {!bugFixSupported ? (
                        <UnsupportedNote>
                            <strong>Not available in this environment.</strong>
                            <br />
                            <small>
                                The bug fix Step Function is deployed in{" "}
                                {BUG_FIX_ENVIRONMENTS.join(", ")} only.
                            </small>
                        </UnsupportedNote>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-3">
                                <label className="flex items-center gap-2 text-sm">
                                    <span className="text-muted-foreground">Action</span>
                                    <Select
                                        value={bug}
                                        onChange={(event) => setBug(event.target.value)}
                                        className="w-auto"
                                    >
                                        <option value="insert">insert</option>
                                        <option value="removal">removal</option>
                                    </Select>
                                </label>
                                <label className="flex items-center gap-2 text-sm">
                                    <span className="text-muted-foreground">Overlay</span>
                                    <Select
                                        value={overlay}
                                        onChange={(event) => setOverlay(event.target.value)}
                                        disabled={bug !== "insert"}
                                        className="w-auto"
                                    >
                                        {OVERLAYS.map((option) => (
                                            <option key={option} value={option}>
                                                {option}
                                            </option>
                                        ))}
                                    </Select>
                                </label>
                                <Button
                                    size="sm"
                                    onClick={handleBugFix}
                                    disabled={bugLoading || !eventId}
                                    className="min-w-[120px] font-medium"
                                >
                                    {bugLoading ? "Starting..." : "Apply"}
                                </Button>
                            </div>
                            <small className="text-muted-foreground">
                                Event <code className="rounded bg-muted px-1.5 py-0.5">{eventId}</code>
                                {bug === "insert" && ` - overlay ${overlay}`}
                            </small>
                            <StatusLine status={bugStatus} />
                        </>
                    )}
                    <ActionLogList entries={logs.bug_fix} error={logsError} />
                </CardContent>
            </Card>

            <Card>
                <CardHeader className="border-b border-border">
                    <CardTitle className="flex items-center gap-2 text-base">
                        <RadioTower className="h-4 w-4 text-primary" />
                        RIST Restart
                    </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 pt-4">
                    {!ristSupported ? (
                        <UnsupportedNote>
                            <strong>Not available in this environment.</strong>
                            <br />
                            <small>
                                The on-prem RIST restart Step Function is deployed in{" "}
                                {RIST_ENVIRONMENTS.join(", ")} only.
                            </small>
                        </UnsupportedNote>
                    ) : !channelId ? (
                        <UnsupportedNote>
                            <strong>No channel found for this event.</strong>
                            <br />
                            <small>
                                The channel name is derived from the event's channel ID, so event
                                details must load first.
                            </small>
                        </UnsupportedNote>
                    ) : (
                        <>
                            <div className="flex flex-wrap items-center gap-3">
                                <span className="text-sm text-muted-foreground">Legs</span>
                                {RIST_LEGS.map((leg) => (
                                    <label key={leg} className="flex items-center gap-1.5 text-sm">
                                        <input
                                            type="checkbox"
                                            checked={legs.includes(leg)}
                                            onChange={() => toggleLeg(leg)}
                                        />
                                        {leg}
                                    </label>
                                ))}
                                <Button
                                    size="sm"
                                    variant="outline-destructive"
                                    onClick={handleRistRestart}
                                    disabled={ristLoading || channels.length === 0}
                                    className="min-w-[120px] font-medium"
                                >
                                    {ristLoading ? "Restarting..." : "Restart"}
                                </Button>
                            </div>
                            <div className="flex flex-wrap items-center gap-2">
                                <small className="text-muted-foreground">Channels</small>
                                {channels.length > 0 ? (
                                    channels.map((channel) => (
                                        <code
                                            key={channel}
                                            className="rounded bg-muted px-1.5 py-0.5 text-sm"
                                        >
                                            {channel}
                                        </code>
                                    ))
                                ) : (
                                    <small className="text-muted-foreground">
                                        Select at least one leg
                                    </small>
                                )}
                            </div>
                            <StatusLine status={ristStatus} />
                        </>
                    )}
                    <ActionLogList entries={logs.rist_restart} error={logsError} />
                </CardContent>
            </Card>
        </div>
    );
};

export default Actions;
